import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { formatDatum } from '../lib/aktivitaet.js';
import {
  fetchAktiveUpdates,
  fetchEigeneBestaetigteUpdateIds,
} from '../lib/updates.js';

export default function UpdatesPage() {
  const { user } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [bestaetigteIds, setBestaetigteIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offen, setOffen] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [list, ids] = await Promise.all([
        fetchAktiveUpdates(),
        fetchEigeneBestaetigteUpdateIds(user.id),
      ]);
      setUpdates(list);
      setBestaetigteIds(ids);
      // Erstes (= neuestes) Update direkt aufgeklappt zeigen, damit die Seite
      // beim Aufruf nicht "leer" wirkt.
      if (list.length > 0) setOffen(list[0].id);
    } catch (err) {
      setError(err?.message || 'Updates konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Neuigkeiten</h1>
        <p className="mt-1 text-sm text-slate-600">
          Was hat sich im CRM zuletzt geändert? Klick auf einen Eintrag öffnet
          die vollständige Beschreibung.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-slate-500">Lade …</p>}

      {!loading && updates.length === 0 && (
        <div className="card text-sm text-slate-500">
          Noch keine Neuigkeiten veröffentlicht.
        </div>
      )}

      <div className="space-y-3">
        {updates.map((update) => {
          const istOffen = offen === update.id;
          const istNeu = !bestaetigteIds.has(update.id);
          return (
            <article
              key={update.id}
              className={`card transition ${istOffen ? 'ring-2 ring-primary-200' : ''}`}
            >
              <button
                type="button"
                onClick={() => setOffen(istOffen ? null : update.id)}
                className="flex w-full flex-wrap items-start justify-between gap-3 text-left"
                aria-expanded={istOffen}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {istNeu && (
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800 ring-1 ring-inset ring-purple-200">
                        Neu
                      </span>
                    )}
                    {update.version && (
                      <span className="text-xs font-medium text-slate-500">
                        Version {update.version}
                      </span>
                    )}
                    {update.veroeffentlicht_am && (
                      <span className="text-xs text-slate-400">
                        · {formatDatum(update.veroeffentlicht_am)}
                      </span>
                    )}
                  </div>
                  <h2 className="mt-1 text-lg font-semibold text-slate-900">
                    {update.titel}
                  </h2>
                </div>
                <span
                  aria-hidden="true"
                  className={`mt-1 text-xl text-slate-400 transition-transform ${
                    istOffen ? 'rotate-180' : ''
                  }`}
                >
                  ⌃
                </span>
              </button>

              {istOffen && (
                <div
                  className="update-content mt-4 border-t border-slate-100 pt-4"
                  dangerouslySetInnerHTML={{ __html: update.inhalt || '' }}
                />
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
